import { Fragment, type ReactNode } from "react";

import type { IntlShape } from "react-intl";

import { Divider } from "@/components/atoms/Divider";
import type { PriceLineItemTooltip } from "@/components/molecules/PriceLineItem";
import { PriceSummary } from "@/components/molecules/PriceSummary/PriceSummary";

import { OutsideBasePricingMessage } from "./OutsideBasePricingMessage";
import { RenderRows } from "./RenderRows";
import type {
  OutsideBaseMessage,
  PriceFacts,
  PriceSummarySection,
  RowId,
} from "./utils/types";

type PriceSummarySectionsProps = {
  currency?: string;
  /**
   * When true, whole row values render without decimals ("$700") while
   * fractional ones keep them ("$52.25"). Amounts in labels, captions and
   * tooltips keep cents. Defaults to false ("$700.00").
   */
  excludeDecimals?: boolean;
  /**
   * Optional facts for detail explainers (e.g. Add-ons total itemization).
   */
  facts?: PriceFacts | null;
  /** Required — consumers own the intl instance (no `useIntl()` here). */
  intl: IntlShape;
  /** P5 inquiry outside-base descriptor from `buildPriceSummary`. */
  outsideBaseMessage?: OutsideBaseMessage;
  /**
   * Adapter wraps the base-pricing link (popover). Only used when
   * `outsideBaseMessage` is set.
   */
  renderBasePricingLink?: (linkLabel: ReactNode) => ReactNode;
  /**
   * Adapter wraps the DIY space rental tooltip's "View pricing rules" link,
   * receiving a `closeTooltip` callback to dismiss the popover before
   * opening the pricing-rules dialog. Omit to render the tooltip's
   * explainer text without a link.
   */
  renderPricingRulesLink?: (
    linkLabel: ReactNode,
    closeTooltip: () => void,
  ) => ReactNode;
  sections: PriceSummarySection[];
  /** When false, strip tooltip/detail explainers. Defaults to true. */
  showExplainers?: boolean;
  testId?: string;
  /**
   * Per-row tooltip override, keyed by `RowId`. When set for a row, it fully
   * replaces that row's catalog-driven tooltip (title/content/link), for
   * call-site copy that doesn't belong in the shared message catalog — e.g.
   * a temporary experiment's explainer. Every other row is unaffected.
   */
  tooltipOverrides?: Partial<Record<RowId, PriceLineItemTooltip>>;
};

/**
 * Renders a `buildPriceSummary` model via bindings → DS `PriceLineItem`.
 * Inserts dividers on `dividerBefore` and between sections.
 * Consumers pass `intl`.
 */
export const PriceSummarySections = ({
  currency = "USD",
  excludeDecimals = false,
  facts,
  intl,
  outsideBaseMessage,
  renderBasePricingLink,
  renderPricingRulesLink,
  sections,
  showExplainers = true,
  testId = "price-summary",
  tooltipOverrides,
}: PriceSummarySectionsProps) => {
  if (outsideBaseMessage != null) {
    return (
      <OutsideBasePricingMessage
        intl={intl}
        message={outsideBaseMessage}
        renderBasePricingLink={renderBasePricingLink}
      />
    );
  }

  if (sections.length === 0) return null;

  return (
    <PriceSummary spacing={12} testId={testId}>
      {sections.map((section, sectionIndex) => (
        <Fragment key={section.id}>
          {sectionIndex > 0 && <Divider spacing={0} />}
          <RenderRows
            currency={currency}
            excludeDecimals={excludeDecimals}
            facts={facts}
            intl={intl}
            renderPricingRulesLink={renderPricingRulesLink}
            rows={section.rows}
            showExplainers={showExplainers}
            testId={`${testId}-${section.id}`}
            tooltipOverrides={tooltipOverrides}
          />
        </Fragment>
      ))}
    </PriceSummary>
  );
};
